import { DEFAULT_REWRITE_OPTIONS, RewriteOptionsSchema, type RewriteOptions } from '@lyrmo/shared';
import { useCallback, useState } from 'react';

const STORAGE_KEY = 'lyrmo.rewrite-options';

function readStored(): RewriteOptions {
  try {
    const parsed = RewriteOptionsSchema.safeParse(
      JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'),
    );
    return parsed.success ? parsed.data : DEFAULT_REWRITE_OPTIONS;
  } catch {
    // Storage can be unavailable, as in private windows, or hold something unreadable.
    return DEFAULT_REWRITE_OPTIONS;
  }
}

/**
 * The rewrite options, remembered in this browser. The instruction isn't: it's usually about
 * one piece of writing, and a forgotten one would quietly change the next. Saved instructions
 * are, as their chips show they're followed.
 */
export function useStoredOptions(): [RewriteOptions, (options: RewriteOptions) => void] {
  const [options, setOptions] = useState(readStored);

  const update = useCallback((next: RewriteOptions) => {
    setOptions(next);
    try {
      const { tone, customTone, length, language, savedInstructions } = next;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ tone, customTone, length, language, savedInstructions }),
      );
    } catch {
      // Remembering is a convenience; the options still apply to this session.
    }
  }, []);

  return [options, update];
}
